import React, { useEffect, useMemo, useState } from 'react';
import MultiChoiceOption from 'components/challenges/multiChoice/MultiChoiceOption';
import ChallengeTag from 'components/ChallengeTag';

import * as lessonParser from 'utils/lessonParser';

import { useAudio } from 'utils/audioContext';

function shuffleArray(arr) {
    const array = arr.slice();
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
}


function Match({ questionData, questionText, questionStatus, questionVariables, onAnswerCheck, manualCheck, tag }) {

    const { play } = useAudio();

    const pairs = useMemo(() => {
        if (!questionData?.data) return [];

        let parsedPairs = []
        for (let i = 0; i < questionData.data.pairs.length; i++) {
            const parsedPair = lessonParser
                .parseValueList(
                    questionData.data.pairs[i],
                    false,
                    questionVariables
                )
            // .map(pair => ({ "left": pair[0]?.value, "right": pair[1]?.value }));
            // console.log("Parsing pair:", parsedPair);
            if (parsedPair.length === 2) {
                parsedPairs.push({
                    left: parsedPair[0]?.value,
                    right: parsedPair[1]?.value
                });
            }
        }

        return shuffleArray(parsedPairs);
    }, [JSON.stringify(questionData?.data?.pairs), questionVariables]);

    const { highlightLeft = true, highlightRight = true } = questionData.data?.options || {};

    const [leftOrder, setLeftOrder] = useState([]);
    const [rightOrder, setRightOrder] = useState([]);
    const [selectedLeft, setSelectedLeft] = useState(null);
    const [selectedRight, setSelectedRight] = useState(null);
    const [connections, setConnections] = useState({}); // leftIdx -> rightIdx

    const [lastPair, setLastPair] = useState(null);

    useEffect(() => {
        setLeftOrder(shuffleArray(pairs.map((_, i) => i)));
        setRightOrder(shuffleArray(pairs.map((_, i) => i)));
        setSelectedLeft(null);
        setSelectedRight(null);
        setConnections({});
    }, [pairs]);

    useEffect(() => {
        onAnswerCheck((force) => {
            if (Object.keys(connections).length !== pairs.length && !force) return false;
            return {
                isCorrect: Object.entries(connections).every(
                    ([l, r]) => pairs[leftOrder[l]].right === pairs[rightOrder[r]].right
                ) && Object.keys(connections).length === pairs.length,
            };
        });
    }, [onAnswerCheck, connections, pairs, leftOrder, rightOrder]);

    function handleSelect(side, index) {
        if (questionStatus.guessed) return;
        if (side === 'left') {
            setSelectedLeft(index);
        } else {
            setSelectedRight(index);
        }
    }

    useEffect(() => {
        if (selectedLeft !== null && selectedRight !== null) {
            setLastPair([selectedLeft, selectedRight]);
            setConnections((prev) => ({ ...prev, [selectedLeft]: selectedRight }));
            setSelectedLeft(null);
            setSelectedRight(null);
        }
    }, [selectedLeft, selectedRight]);

    useEffect(() => {
        if (!connections || Object.keys(connections).length === 0) return;
        if (lastPair) {
            const [leftIdx, rightIdx] = lastPair;
            const correct = pairs[leftOrder[leftIdx]].right === pairs[rightOrder[rightIdx]].right;
            if (correct) {
                if (Object.keys(connections).length === pairs.length) {
                    console.log('All pairs matched correctly!');
                    manualCheck();
                } else {
                    play('correct.mp3');
                }
            } else {
                play('incorrect.mp3');
                setTimeout(() => {
                    setConnections((prev) => {
                        const newConnections = { ...prev };
                        delete newConnections[leftIdx];
                        return newConnections;
                    });
                }, 500);
            }
        }
        setLastPair(null);
    }, [connections]);

    const getStatusFor = (side, idx) => {
        let foundIndex = side === 'left'
            ? Object.keys(connections).find(conn => conn == idx)
            : Object.values(connections).find(conn => conn === idx);
        if (side === 'left') {
            const paired = connections[idx];
            if (paired === undefined) return { guessed: false, answer: false };
            const correct = pairs[leftOrder[idx]].right === pairs[rightOrder[paired]].right;
            return { guessed: foundIndex !== undefined, answer: correct };
        } else {
            const paired = Object.keys(connections).find(conn => connections[conn] === idx);
            if (paired === undefined) return { guessed: false, answer: false };
            const correct = pairs[leftOrder[paired]].right === pairs[rightOrder[idx]].right;
            return { guessed: foundIndex !== undefined, answer: correct };
        }
    };

    return (
        <div className='text-text flex flex-col items-center justify-between w-full break-small:w-[900px] break-big:w-[1000px] px-8 break-small:px-2 max-h-full py-2'>
            <div className='px-10 break-small:px-0 w-full text-center break-small:text-left mb-[6vh]'>
                {tag && <div className='mb-3'><ChallengeTag tag={tag} /></div>}
                <h1 className='font-bold text-[4vw] break-small:text-4xl'>
                    {questionText}
                </h1>
            </div>
            <div className='flex justify-between gap-6 break-small:gap-20 break-big:gap-4 w-full'>
                <div className='grid gap-3 w-full break-big:w-2/5 min-w-72'>
                    {leftOrder.map((origIdx, i) => (
                        <MultiChoiceOption
                            key={`l-${i}`}
                            text={pairs[origIdx].left}
                            optionIndex={i}
                            questionStatus={getStatusFor('left', i)}
                            selected={selectedLeft == i || Object.keys(connections).find(conn => conn == i) !== undefined ? i : null}
                            setSelected={() => handleSelect('left', i)}
                            forceCenter={true}
                            options={{ highlight: highlightLeft }}
                        />
                    ))}
                </div>
                <div className='grid gap-3 w-full break-big:w-2/5 min-w-72'>
                    {rightOrder.map((origIdx, i) => (
                        <MultiChoiceOption
                            key={`r-${i}`}
                            text={pairs[origIdx].right}
                            optionIndex={i}
                            questionStatus={getStatusFor('right', i)}
                            selected={selectedRight === i || Object.values(connections).find(conn => conn === i) !== undefined ? i : null}
                            setSelected={() => handleSelect('right', i)}
                            forceCenter={true}
                            options={{ highlight: highlightRight }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default Match;
